import React from 'react';
import { formatCurrency } from '../../lib/utils';

export const COMPANY_INFO = {
  name: 'C.S.M Sh.p.k.',
  address: 'Tirane Vore Muçaj Rruga dytesore, Tirane -Vore, 1032, Vore, Tirane.',
  city: '',
  nipt: '',
  phone: '+355 68 200 1818',
  email: 'info@albmove.al',
  website: 'www.albmove.al',
  bankAccount: 'AL47 2121 1009 0000 0002 3569 8741',
  bankName: 'Raiffeisen Bank',
};

export const formatEuropeanDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return '—';
  }
};

export const formatEuropeanDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

export const generateInvoiceNumber = (invoice) => {
  if (!invoice) return 'N/A';
  if (invoice.invoice_number) return invoice.invoice_number;
  const sourceDate = invoice.end_time || invoice.start_time || invoice.created_at || new Date().toISOString();
  const date = new Date(sourceDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const id = String(invoice.id || 'XXXXXX').slice(0, 6).toUpperCase();
  return `ALB-${year}${month}-${id}`;
};

const roundTo = (value, decimals = 2) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? Number(number.toFixed(decimals)) : 0;
};

const isTopupType = (value) => {
  const type = String(value || '').toLowerCase();
  return (
    type.includes('topup') ||
    type.includes('recharge') ||
    type.includes('business_credit') ||
    type.includes('business_employee_allocation')
  );
};

const resolveInvoiceType = (source) => {
  if (!source) return 'charging';
  const explicitType = source.invoice_type || source.type || source.transaction_type;
  if (isTopupType(explicitType)) return 'topup';
  return 'charging';
};

export const normalizeChargingInvoice = (source) => {
  if (!source) return null;

  const invoiceType = resolveInvoiceType(source);
  const providerRaw = `${String(source.payment_provider || '').toLowerCase()} ${String(source.processor || '').toLowerCase()} ${String(source.payment_method || '').toLowerCase()}`;
  const mergedType = `${String(source.type || '').toLowerCase()} ${String(source.invoice_type || '').toLowerCase()}`;
  const isTopupInvoice = invoiceType === 'topup';
  const isCashTopup = isTopupInvoice && (mergedType.includes('cash_topup') || providerRaw.includes('cash'));

  const resolvedClientName = pickDisplayValue(
    source.client_name,
    source.user_name,
    source.full_name,
    source.client?.full_name,
    source.user?.full_name,
    source.customer_name,
    source.customer?.full_name
  );
  const resolvedClientEmail = pickDisplayValue(
    source.client_email,
    source.user_email,
    source.email,
    source.client?.email,
    source.user?.email,
    source.customer_email,
    source.customer?.email
  );
  const resolvedClientPhone = pickDisplayValue(
    source.user_phone,
    source.client_phone,
    source.phone,
    source.mobile,
    source.client?.phone,
    source.user?.phone,
    source.customer_phone,
    source.customer?.phone
  );

  const startTime = source.start_time || source.started_at || source.created_at || source.paid_at || null;
  const endTime = source.end_time || source.completed_at || source.stopped_at || source.paid_at || source.created_at || null;
  const derivedDuration = startTime && endTime
    ? Math.max(0, Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000))
    : null;
  const energyKwh = roundTo(
    source.energy_kwh ?? (source.energy_wh ? Number(source.energy_wh) / 1000 : 0)
  );
  const creditedAmount = roundTo(source.credited_amount ?? source.amount ?? source.cost_lek ?? 0);
  const feeAmount = roundTo(source.fee ?? source.fee_lek ?? 0);
  const topupTotalAmount = roundTo(source.total ?? source.total_amount ?? source.total_with_vat ?? (creditedAmount + feeAmount));
  const isPokTopup = isTopupInvoice && !isCashTopup && (
    providerRaw.includes('pok') ||
    feeAmount > 0 ||
    topupTotalAmount > creditedAmount
  );

  const rawTotalCost = roundTo(
    isTopupInvoice
      ? (isPokTopup ? (topupTotalAmount || (creditedAmount + feeAmount)) : (source.amount ?? source.cost_lek ?? creditedAmount))
      : (source.cost_lek ?? source.total_cost ?? source.amount ?? 0)
  );
  const tariff = roundTo(
    source.tariff_lek_per_kwh ??
    source.price_per_kwh ??
    source.charger_price_per_kwh ??
    source.current_price_per_kwh ??
    source.charger?.price_per_kwh ??
    ((energyKwh > 0 && rawTotalCost > 0) ? rawTotalCost / energyKwh : 40)
  );
  // Use stored cost directly — only calculate if stored value is genuinely missing/zero
  const totalCost = rawTotalCost > 0
    ? rawTotalCost
    : roundTo(energyKwh > 0 && tariff > 0 ? energyKwh * tariff : 0);

  return {
    ...source,
    invoice_type: invoiceType,
    start_time: startTime,
    end_time: endTime,
    created_at: source.created_at || endTime || startTime,
    client_name: resolvedClientName,
    client_email: resolvedClientEmail,
    client_phone: resolvedClientPhone,
    user_name: resolvedClientName,
    user_email: resolvedClientEmail,
    user_phone: resolvedClientPhone,
    duration_minutes: source.duration_minutes ?? derivedDuration,
    energy_kwh: energyKwh,
    cost_lek: totalCost,
    amount: isTopupInvoice ? creditedAmount : totalCost,
    credited_amount: creditedAmount,
    fee: feeAmount,
    total: isTopupInvoice ? (topupTotalAmount || totalCost) : totalCost,
    is_pok_topup: isPokTopup,
    tariff_lek_per_kwh: tariff,
    charger_price_per_kwh: source.charger_price_per_kwh ?? source.current_price_per_kwh ?? source.charger?.price_per_kwh ?? null,
    charger_name: source.charger_name || source.charger_ocpp_id || source.charger_id || 'N/A',
    charger_id: source.charger_id || source.charger_ocpp_id || source.charger_name || 'N/A',
    site_name: source.site_name || 'N/A',
    payment_provider: source.payment_provider || source.processor || null,
    payment_method: source.payment_method || (invoiceType === 'topup' ? 'Cash' : 'Wallet'),
    transaction_id: source.transaction_id || source.reference_id || null,
    wallet_type: source.wallet_type || null,
    sender_name: source.sender_name || source.business_name || source.moderator_name || source.processed_by_name || null,
    moderator_name: source.moderator_name || source.processed_by_name || null,
  };
};

const pickDisplayValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const text = String(value).trim();
    if (text) {
      return text;
    }
  }

  return '';
};

const formatCreditsQuantity = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0 Kredite';
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
  return `${formatted} Kredite`;
};

const ChargingInvoiceDocument = ({ invoice }) => {
  const normalizedInvoice = normalizeChargingInvoice(invoice);

  if (!normalizedInvoice) {
    return null;
  }

  const billedToName = pickDisplayValue(
    normalizedInvoice.client_name,
    normalizedInvoice.user_name,
    normalizedInvoice.full_name,
    normalizedInvoice.client?.full_name,
    normalizedInvoice.user?.full_name,
    'Klient'
  );
  const billedToEmail = pickDisplayValue(
    normalizedInvoice.client_email,
    normalizedInvoice.user_email,
    normalizedInvoice.email,
    normalizedInvoice.client?.email,
    normalizedInvoice.user?.email
  );
  const billedToPhone = pickDisplayValue(
    normalizedInvoice.user_phone,
    normalizedInvoice.client_phone,
    normalizedInvoice.phone,
    normalizedInvoice.client?.phone,
    normalizedInvoice.user?.phone
  );
  const billedToIdCard = pickDisplayValue(
    normalizedInvoice.id_card,
    normalizedInvoice.id_number,
    normalizedInvoice.personal_id,
    normalizedInvoice.client_id_card,
    normalizedInvoice.user_id_card,
    normalizedInvoice.client?.id_card,
    normalizedInvoice.user?.id_card
  );
  const billedToAddress = pickDisplayValue(
    normalizedInvoice.address,
    normalizedInvoice.street_address,
    normalizedInvoice.client_address,
    normalizedInvoice.user_address,
    normalizedInvoice.client?.address,
    normalizedInvoice.user?.address,
    normalizedInvoice.city,
    normalizedInvoice.client?.city,
    normalizedInvoice.user?.city
  );

  const isTopupInvoice = normalizedInvoice.invoice_type === 'topup';
  const isPokTopup = isTopupInvoice && (
    normalizedInvoice.is_pok_topup ||
    String(normalizedInvoice.payment_provider || normalizedInvoice.processor || '').toLowerCase().includes('pok') ||
    String(normalizedInvoice.payment_method || '').toLowerCase().includes('pok') ||
    Number(normalizedInvoice.fee || 0) > 0 ||
    Number(normalizedInvoice.total || 0) > Number(normalizedInvoice.credited_amount || normalizedInvoice.amount || 0)
  );
  const invoiceCurrency = isTopupInvoice ? 'LEK' : 'Kredite';
  const tariffCurrency = isTopupInvoice ? 'LEK/kWh' : 'Kredite/kWh';
  const detailsTitle = isTopupInvoice
    ? 'Detajet e Rimbushjes së Portofolit'
    : 'Detajet e Seancës së Karikimit';

  const topupDate = normalizedInvoice.paid_at || normalizedInvoice.created_at || normalizedInvoice.end_time || normalizedInvoice.start_time;
  const topupWalletLabel = normalizedInvoice.wallet_type === 'restricted'
    ? 'Balancë për Stacion'
    : 'Balancë e Përgjithshme';
  const topupTypeLabel = String(normalizedInvoice.type || '').toLowerCase().includes('cash')
    ? 'Rimbushje Cash'
    : 'Blerje kreditesh';
  const purchasedCredits = Number(
    isPokTopup
      ? (normalizedInvoice.credited_amount ?? normalizedInvoice.amount ?? 0)
      : (normalizedInvoice.amount ?? normalizedInvoice.cost_lek ?? 0)
  );
  const handlingFee = Number(normalizedInvoice.fee ?? 0);
  const invoiceTotal = Number(normalizedInvoice.total ?? normalizedInvoice.cost_lek ?? 0);
  const topupDetails = isPokTopup
    ? [
        ['Lloji i Rimbushjes', topupTypeLabel],
        ['Data e Rimbushjes', formatEuropeanDateTime(topupDate)],
        ['Kredite të Blera', formatCreditsQuantity(purchasedCredits)],
        ['Raporti', '1 Kredit = 1 Lek'],
        ['Destinacioni i Balancës', topupWalletLabel],
        ['Referenca', normalizedInvoice.transaction_id || normalizedInvoice.id || 'N/A'],
      ]
    : [
        ['Stacioni', normalizedInvoice.site_name || 'N/A'],
        ['Lloji i Rimbushjes', topupTypeLabel],
        ['Data e Rimbushjes', formatEuropeanDateTime(topupDate)],
        ['Kredite të Blera', formatCreditsQuantity(purchasedCredits)],
        ['Raporti', '1 Kredit = 1 Lek'],
        ['Destinacioni i Balancës', topupWalletLabel],
        ['Dërguesi', normalizedInvoice.sender_name || normalizedInvoice.moderator_name || 'N/A'],
        ['Procesuar nga', normalizedInvoice.moderator_name || normalizedInvoice.sender_name || 'N/A'],
        ['Referenca', normalizedInvoice.transaction_id || normalizedInvoice.id || 'N/A'],
        ['Shënime', normalizedInvoice.notes || normalizedInvoice.description || '—'],
      ];

  const chargingDetails = [
    ['Stacioni', normalizedInvoice.site_name || 'N/A'],
    ['Karikuesi ID', normalizedInvoice.charger_name || normalizedInvoice.charger_id || 'N/A'],
    ['Fillimi i Karikimit', formatEuropeanDateTime(normalizedInvoice.start_time)],
    ['Mbarimi i Karikimit', formatEuropeanDateTime(normalizedInvoice.end_time)],
    ['Kohëzgjatja', normalizedInvoice.duration_minutes ? `${Math.round(normalizedInvoice.duration_minutes)} minuta` : 'N/A'],
    ['Energji e Konsumuar', `${normalizedInvoice.energy_kwh.toFixed(2)} kWh`],
    ['Tarifa', `${normalizedInvoice.tariff_lek_per_kwh.toFixed(2)} ${tariffCurrency}`],
  ];

  const detailsRows = isTopupInvoice ? topupDetails : chargingDetails;

  return (
    <div className="invoice-document bg-white text-gray-900 p-5">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-shrink-0">
          <div className="h-14 w-14 overflow-hidden rounded-md">
            <img
              src="/logo192.png"
              alt="AlbMove"
              className="h-full w-full object-contain scale-[1.18]"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5">Sistemi i Karikimit Elektrik</p>
        </div>

        <h2 className="text-right text-base font-bold text-gray-900 leading-tight max-w-[72%] mt-0.5">
          {isTopupInvoice ? 'Fature rimbushje kreditesh AlbMove' : 'Faturë debitimi kreditesh AlbMove'}
        </h2>
      </div>

      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-xs text-gray-500 uppercase">Nr. Faturës</p>
          <p className="font-bold text-[#1d62a7]">{generateInvoiceNumber(normalizedInvoice)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 uppercase">Data</p>
          <p className="font-medium">{formatEuropeanDate(normalizedInvoice.end_time || normalizedInvoice.start_time)}</p>
        </div>
      </div>

      <div className="h-1 bg-gradient-to-r from-[#1d62a7] to-[#10b981] rounded-full mb-4"></div>

      <div className="grid grid-cols-2 gap-2 mb-5">
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm font-bold text-gray-900">{COMPANY_INFO.name}</p>
          <p className="text-sm text-gray-600 mt-0.5"><b>Adresa: </b> {COMPANY_INFO.address}</p>
          <p className="text-sm text-gray-600 mt-0.5"><b>Nr. Cel: </b> {COMPANY_INFO.phone}</p>
          <p className="text-sm text-gray-600 mt-0.5"><b>Email: </b> {COMPANY_INFO.email}</p>
        </div>

        <div className="bg-gray-50 p-3 rounded-lg">
          <h3 className="text-xs font-semibold text-[#1d62a7] uppercase tracking-wider mb-1.5">
            Faturuar Drejt
          </h3>
          <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900"></span> {billedToName}</p>
          <p className="text-sm text-gray-600 mt-0.5"><span className="font-semibold text-gray-900"></span> {billedToIdCard || 'N/A'}</p>
          <p className="text-sm text-gray-600 mt-0.5"><span className="font-semibold text-gray-900"></span> {billedToEmail || 'N/A'}</p>
          {billedToPhone ? (
            <p className="text-sm text-gray-600 mt-0.5"><span className="font-semibold text-gray-900"></span> {billedToPhone}</p>
          ) : null}
          <p className="text-sm text-gray-600 mt-0.5"><span className="font-semibold text-gray-900"></span> {billedToAddress || 'N/A'}</p>
        </div>
      </div>

      <div className="mb-5">
        <h3 className="text-xs font-semibold text-[#1d62a7] uppercase tracking-wider mb-2">
          {detailsTitle}
        </h3>
        <div className="bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {detailsRows.map(([label, value], index) => {
                const isLast = index === detailsRows.length - 1;
                const isEnergyRow = label === 'Energji e Konsumuar';
                const isChargerRow = label === 'Karikuesi ID';
                return (
                  <tr key={label} className={isLast ? '' : 'border-b border-gray-200'}>
                    <td className="py-1.5 px-2.5 text-gray-600">{label}</td>
                    <td
                      className={`py-1.5 px-2.5 text-right ${
                        isEnergyRow
                          ? 'font-bold text-[#1d62a7]'
                          : isChargerRow
                            ? 'font-mono'
                            : 'font-medium'
                      }`}
                    >
                      {value}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isTopupInvoice ? (
        <div className="mb-5">
          <h3 className="text-xs font-semibold text-[#1d62a7] uppercase tracking-wider mb-2">
            Artikulli i Faturës
          </h3>
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-[#1d62a7] text-white">
                <th className="text-left py-1.5 px-2.5 font-semibold">Përshkrimi</th>
                <th className="text-center py-1.5 px-2.5 font-semibold">Sasia</th>
                <th className="text-right py-1.5 px-2.5 font-semibold">Çmimi</th>
                <th className="text-right py-1.5 px-2.5 font-semibold">Totali</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td className="py-2.5 px-2.5">
                  <p className="font-medium text-gray-900">{isPokTopup ? 'TopUp Kredite Albmove' : 'Blerje Kreditesh AlbMove'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{`Blere ${formatCreditsQuantity(purchasedCredits)}, 1 Kredit = 1 Lek`}</p>
                </td>
                <td className="text-center py-2.5 px-2.5 font-medium">{formatCreditsQuantity(purchasedCredits)}</td>
                <td className="text-right py-2.5 px-2.5">1.00 Lek</td>
                <td className="text-right py-2.5 px-2.5 font-bold text-[#1d62a7]">
                  {formatCurrency(purchasedCredits, invoiceCurrency)}
                </td>
              </tr>
              {isPokTopup && handlingFee > 0 ? (
                <tr className="bg-white border-t border-gray-200">
                  <td className="py-2 px-2.5">
                    <p className="font-medium text-gray-900">Handling fee POK</p>
                    <p className="text-xs text-gray-500 mt-0.5">2.5% + 20 LEK</p>
                  </td>
                  <td className="text-center py-2 px-2.5 font-medium">1</td>
                  <td className="text-right py-2 px-2.5">{formatCurrency(handlingFee, invoiceCurrency)}</td>
                  <td className="text-right py-2 px-2.5 font-bold text-amber-600">
                    {formatCurrency(handlingFee, invoiceCurrency)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex justify-end mb-4">
        <div className="w-60 max-w-full">
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            <div className="flex justify-between py-1.5 px-2.5 border-b border-gray-200 text-sm">
              <span className="text-gray-600">Nëntotali</span>
              <span className="font-medium">{formatCurrency(isPokTopup ? purchasedCredits : normalizedInvoice.cost_lek, invoiceCurrency)}</span>
            </div>
            {isPokTopup && handlingFee > 0 ? (
              <div className="flex justify-between py-1.5 px-2.5 border-b border-gray-200 text-sm">
                <span className="text-gray-600">Handling fee</span>
                <span className="font-medium text-amber-600">{formatCurrency(handlingFee, invoiceCurrency)}</span>
              </div>
            ) : null}
          </div>
          <div className="flex justify-between items-center py-2 px-3 bg-gradient-to-r from-[#1d62a7] to-[#1a5490] text-white rounded-lg mt-1">
            <span className="text-sm font-semibold">TOTALI</span>
            <span className="text-lg font-bold">{formatCurrency(isPokTopup ? invoiceTotal : normalizedInvoice.cost_lek, invoiceCurrency)}</span>
          </div>
        </div>
      </div>

      <div className="invoice-payment-status flex items-center gap-2 mb-2.5">
        <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          E Paguar
        </div>
        <span className="text-sm text-gray-500">Metoda: {normalizedInvoice.payment_method || (isTopupInvoice ? 'Cash' : 'Wallet')}</span>
      </div>

      <div className="pt-2 border-t border-gray-200 text-center">
        <p className="text-[#1d62a7] text-sm font-semibold leading-tight">Faleminderit që zgjodhët AlbMove!</p>
        <p className="text-gray-500 text-[11px] leading-tight mt-1">
          {COMPANY_INFO.website} | {COMPANY_INFO.email} | {COMPANY_INFO.phone}
        </p>
        <p className="text-gray-400 text-[10px] leading-tight mt-1">
          Kjo faturë është gjeneruar automatikisht dhe është e vlefshme pa nënshkrim.
        </p>
      </div>
    </div>
  );
};

export default ChargingInvoiceDocument;